/**
 * COMPTABILITÉ — CE QUE DIT LE PANNEAU « BILLETTERIE DES ASSOCIATIONS »
 * (revue du 16/10/2026). Pur, sans React : testé sans navigateur
 * (`tests/finance-billetterie-associations.test.ts`).
 */

import type { AssociationsVue } from './BilletterieAssociations.js'

/**
 * CE QUE LA NUIT A DÉJÀ FAIT DE SON CHIFFRE — le panneau ne le devine pas
 * depuis la billetterie seule (revue du 16/10) :
 *   · `REPRISE` : validée au Total du suivi de la DAF (le 09/09 : billetterie
 *     des associations comprise) — dire « hors du chiffre » était faux ;
 *   · `VALIDEE` : validée sans clôture détaillée — son chiffre est celui de
 *     la validation, rien n'y est ajouté ;
 *   · `FINALISEE_SANS` : clôture finalisée avant que Gestion compte cette
 *     billetterie — « entre au chiffre à la finalisation » était faux.
 */
export type SituationNuitAssociations = 'REPRISE' | 'VALIDEE' | 'FINALISEE_SANS' | null

/** La pastille : ce que la nuit fait de cette billetterie, en trois mots. */
export function pastille(a: AssociationsVue, situation: SituationNuitAssociations): { ton: string; texte: string } {
  if (a.frozen) return a.revenue_cents !== null && a.revenue_cents !== 0
    ? { ton: 'lc-pill--ok', texte: 'Arrêtée à la finalisation' }
    : { ton: 'lc-pill--idle', texte: 'Finalisée hors du chiffre' }
  if (situation === 'FINALISEE_SANS') return { ton: 'lc-pill--idle', texte: 'Finalisée sans cette billetterie' }
  if (situation === 'REPRISE' || situation === 'VALIDEE') return { ton: 'lc-pill--info', texte: 'Nuit validée : rien d’ajouté' }
  // Lecture éteinte, en retard ou pas encore passée après la nuit : jamais vert.
  if (!a.settled) return { ton: 'lc-pill--warning', texte: 'Lecture Pretix incomplète' }
  if (!a.revenue_enabled) return { ton: 'lc-pill--idle', texte: 'Hors du chiffre de la nuit' }
  // Pas encore arrêtée : elle compte en direct sur la fiche, et n'est arrêtée
  // qu'à la finalisation de sa clôture.
  return { ton: 'lc-pill--ok', texte: 'Au chiffre de la nuit' }
}

/** L'introduction : la conséquence d'abord, selon ce que la nuit a déjà fait. */
export function introduction(a: AssociationsVue, situation: SituationNuitAssociations): string {
  const repere = 'La valeur faciale est ce que les acheteurs ont payé : un repère.'
  if (a.frozen) {
    return a.revenue_enabled
      ? `Ce qui reste après la part de chaque association est entré au chiffre de la nuit, tel que la finalisation l’a arrêté. ${repere}`
      : `La finalisation l’a arrêtée hors du chiffre de la nuit (réglage « Billetterie associations dans le CA » éteint à ce moment). ${repere}`
  }
  const siCloture = a.revenue_enabled
    ? 'une clôture détaillée finalisée le remplacerait, et reprendrait cette billetterie par sa ligne à part.'
    : 'une clôture détaillée finalisée le remplacerait sans reprendre cette billetterie (réglage « Billetterie associations dans le CA » éteint) : la finalisation demanderait alors un motif.'
  switch (situation) {
    case 'FINALISEE_SANS':
      return `La clôture de cette nuit a été finalisée avant que Gestion compte la billetterie des associations : son chiffre arrêté ne la contient pas. Les billets ci-dessous sont relus pour information ; seule une révision de la clôture la reprendrait${a.revenue_enabled ? '' : ', réglage « Billetterie associations dans le CA » allumé'}.`
    case 'REPRISE':
      return `Le chiffre de cette nuit est le total repris du suivi de la DAF : il contient déjà ce que la DAF a compté pour cette billetterie, et rien n’y est ajouté ici. Plus tard, ${siCloture}`
    case 'VALIDEE':
      return `Cette nuit est validée sans clôture détaillée : son chiffre est celui de la validation, rien n’y est ajouté ici. Plus tard, ${siCloture}`
    default:
      return a.revenue_enabled
        ? `Ce qui reste après la part de chaque association compte dans le chiffre de la nuit : en direct sur la fiche, arrêté à la finalisation de sa clôture. ${repere}`
        : `Hors du chiffre de la nuit : le réglage « Billetterie associations dans le CA » est éteint. ${repere} Elle n’est pas additionnée.`
  }
}

/**
 * Elle compte dans le chiffre : le RÉGLAGE le dit (ou le gel), pas la
 * présence du montant — cachée par les droits, elle reste « au chiffre »,
 * montant « réservé » (revue du 16/10 : elle se disait « réglage éteint »).
 */
export function dansLeChiffre(a: AssociationsVue, situation: SituationNuitAssociations): boolean {
  return a.frozen ? a.revenue_enabled && a.revenue_cents !== null : situation === null && a.revenue_enabled
}

/** Le sous-titre de la tuile retenue : pourquoi le montant est (ou n'est pas) additionné. */
export function sousRetenu(a: AssociationsVue, situation: SituationNuitAssociations): string {
  return dansLeChiffre(a, situation) ? 'Sur sa propre ligne, à côté de Shotgun'
    : a.frozen ? 'Arrêtée hors du chiffre'
      : situation === 'FINALISEE_SANS' ? 'Pas dans le chiffre arrêté'
        : situation !== null ? 'Rien d’ajouté : nuit déjà validée'
          : 'Hors du chiffre : réglage éteint'
}
